'use client';

import { usePathname } from 'next/navigation';
import type { JSX } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { BasicPopover } from '@/app_components/common/BasicPopover';
import { Icon } from '@/app_components/common/Icon';
import type { IconName } from '@/app_components/icons/constants';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

interface SocialLinkProps {
  href: string;
  name: string;
  icon: IconName;
  className: string;
  dataCy?: ContentPlacement;
}

const SocialLink = ({ href, name, className, dataCy, icon }: SocialLinkProps) => {
  return (
    <ActionableIcon
      aria-label={icon === 'Mail' ? 'Share via email' : `Share on ${name}`}
      as='a'
      data-cy={dataCy}
      href={href || ''}
      rel='noopener noreferrer'
      target='_blank'
    >
      <Icon
        className={className}
        icon={icon}
        mode='fill'
      />
    </ActionableIcon>
  );
};

interface SocialLinksDropdownProps {
  title: string;
  className?: string;
  iconClassName?: string;
  contentClassName?: string;
  isShareButtonHovered?: boolean;
  trigger?: JSX.Element;
}

const getSocialLinks = (pathname: string | null, title: string) => {
  if (!pathname) {
    return [];
  }
  const pageUrl = `https://fortune.com${pathname}`;
  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(title);
  const socialLinks: {
    href: string;
    name: string;
    icon: IconName;
    iconClassName: string;
    dataCy: ContentPlacement | undefined;
  }[] = [
    {
      dataCy: ContentPlacement.FacebookIcon,
      href: `https://www.facebook.com/sharer.php/?u=${encodedUrl}`,
      icon: 'Facebook',
      iconClassName: '',
      name: 'Facebook',
    },
    {
      dataCy: ContentPlacement.TwitterIcon,
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      icon: 'Twitter',
      iconClassName: '',
      name: 'Twitter',
    },
    {
      dataCy: ContentPlacement.LinkedinIcon,
      href: `https://www.linkedin.com/shareArticle/?url=${encodedUrl}`,
      icon: 'LinkedIn',
      iconClassName: '',
      name: 'LinkedIn',
    },
    {
      dataCy: ContentPlacement.EmailIcon,
      href: `mailto:?subject=${title}&body=${encodedUrl}`,
      icon: 'Mail',
      // TODO: replace with a custom icon
      iconClassName: 'stroke-white dark:stroke-black',
      name: 'mailto',
    },
  ];
  return socialLinks;
};

export const SocialLinksDropdown = ({
  title,
  className,
  iconClassName,
  contentClassName,
  isShareButtonHovered,
  trigger,
}: SocialLinksDropdownProps) => {
  const pathname = usePathname();
  const socialLinksList = getSocialLinks(pathname, title);

  const defaultTrigger = (
    <ActionableIcon
      aria-label='Share article'
      className={cn('md:m-2', className)}
      data-cy={ContentPlacement.ShareIcon}
    >
      <Icon
        className={iconClassName}
        icon='Share2'
      />
    </ActionableIcon>
  );

  const content = (
    <div className='flex md:mt-2 h-auto min-w-[40px] animate-slide-fade-in flex-col items-center md:space-y-4 rounded-sm border-1 stroke-secondary border-solid bg-pure py-2'>
      {socialLinksList.map((socialLink) => (
        <SocialLink
          className={socialLink.iconClassName}
          dataCy={socialLink.dataCy}
          href={socialLink.href || ''}
          icon={socialLink.icon}
          key={socialLink.name}
          name={socialLink.name}
        />
      ))}
    </div>
  );

  return (
    <BasicPopover
      align='center'
      content={content}
      contentClassName={cn('z-body-popover mt-5', contentClassName)}
      isShareButtonHovered={isShareButtonHovered}
      shouldCloseOnScroll={false}
      trigger={trigger || defaultTrigger}
    />
  );
};
